import { Hub } from 'aws-amplify/utils';
import { safeReturnPath } from '../utils/returnTo';

/**
 * A lapsed session goes back to the page it was on. (#1039)
 *
 * Refresh tokens last 30 days from sign-in. When one has lapsed (or been revoked), Amplify
 * reports tokenRefresh_failure, and from Amplify UI 6.15.6 that signs out even a page that
 * is still loading. For a Google or Apple account that sign-out goes through Cognito's
 * /logout and back to the site's root: someone opening /november, a help article or their
 * books from a link landed on the home page instead. Up to 6.15.5 they stayed on the page,
 * signed out, and a protected page sent them to sign in and back (#948).
 *
 * The listener notes the page when the sign-out may be coming, and App sends the root back
 * to it, signed out, once the page has loaded again: public pages show as they would to
 * anyone signed out, and protected ones go through sign-in and return as before. The note
 * is only acted on by a later page load (the Cognito round trip takes seconds) and only
 * for 30 seconds, so an email account, signed out where it stands, is all but never sent
 * anywhere by it.
 */

const KEY = 'chaptery.refreshSignOut';
const MAX_AGE_MS = 30 * 1000;
// This page load. A note from an earlier load means the page has loaded again since; one
// from this load means it never left.
const THIS_LOAD = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;

interface Note {
  path: string;
  at: number;
  load: string;
}

/**
 * Notes the page on a refresh failure that can sign out: every one but a network error,
 * which Amplify keeps the session through for a retry (Amplify UI's sign-out rule). Without
 * the hash, as ProtectedRoute and AuthPage keep paths. Starts with the app, in index.tsx.
 * Returns the unsubscribe.
 */
export function listenForRefreshFailure(): () => void {
  return Hub.listen('auth', ({ payload }) => {
    if (payload.event !== 'tokenRefresh_failure') return;
    const error = (payload.data as { error?: { name?: string } } | undefined)?.error;
    if (error?.name === 'NetworkError') return;
    const path = safeReturnPath(window.location.pathname + window.location.search);
    if (!path) return;
    const note: Note = { path, at: Date.now(), load: THIS_LOAD };
    try {
      sessionStorage.setItem(KEY, JSON.stringify(note));
    } catch {
      // Storage refused (a private window, say): the root it is, as without this.
    }
  });
}

/** The noted page, if the note is from an earlier load and recent; else null. */
const pageFrom = (raw: string | null, now: number): string | null => {
  if (!raw) return null;
  let note: Partial<Note>;
  try {
    note = JSON.parse(raw);
  } catch {
    return null;
  }
  if (note.load === THIS_LOAD || typeof note.at !== 'number') return null;
  if (note.at > now || now - note.at > MAX_AGE_MS) return null;
  return safeReturnPath(note.path);
};

/** The page a refresh-failure sign-out carried to the root, without using the note up. */
export function peekRefreshSignOutPage(now = Date.now()): string | null {
  try {
    return pageFrom(sessionStorage.getItem(KEY), now);
  } catch {
    return null;
  }
}

/**
 * The page a refresh-failure sign-out carried to the root, if the page has loaded since
 * and it is recent; otherwise null. Read once: the note is gone either way.
 */
export function takeRefreshSignOutPage(now = Date.now()): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  return pageFrom(raw, now);
}
